"use client";
import { useState, Dispatch, SetStateAction } from "react";
import { ListingTypeEnum } from "@/types/types";
import styles from "./listing-settings-modal.module.css";
import { toast } from "react-toastify";
import { PendingChange } from "../../../types";
import { capitalize } from "@/lib/capitalize-text";

interface Props {
  listingData: any;
  plan: ListingTypeEnum;
  setPlanAction: Dispatch<SetStateAction<ListingTypeEnum>>;
  userTappedCancel: boolean;
  setUserTappedCancelAction: Dispatch<SetStateAction<boolean>>;
  pendingChange?: PendingChange;
  alreadyCanceled?: boolean;
}

export default function ActiveSubscriptionSection({
  listingData,
  plan,
  setPlanAction,
  userTappedCancel,
  setUserTappedCancelAction,
  pendingChange,
  alreadyCanceled,
}: Props) {
  const oldPlan = listingData.listing_type?.toLowerCase();
  const upgrade = planRank(plan) > planRank(oldPlan);
  const downgrade = planRank(plan) < planRank(oldPlan);

  return (
    <>
      <label className={styles.selectLabel}>Change Plan</label>
      <select
        className={styles.select}
        value={plan}
        onChange={(e) => {
          if (userTappedCancel) {
            toast.error(
              "You cannot change your plan after deciding to cancel it.",
            );
            return;
          }
          if (pendingChange) {
            toast.error(
              "You cannot change your plan after already deciding to change it.",
            );
            return;
          }
          setPlanAction(e.target.value as ListingTypeEnum);
        }}
        onMouseDown={(e) => {
          if (userTappedCancel) {
            e.preventDefault(); // stop the dropdown from opening
            e.stopPropagation();
            toast.error(
              "You cannot change your plan after deciding to cancel it.",
            );
          }
          if (pendingChange) {
            e.preventDefault(); // stop the dropdown from opening
            e.stopPropagation();
            toast.error(
              "You cannot change your plan after already deciding to change it.",
            );
          }
        }}
        onTouchStart={(e) => {
          if (userTappedCancel) {
            e.preventDefault(); // prevent dropdown open on mobile
            e.stopPropagation();
            toast.error(
              "You cannot change your plan after deciding to cancel it.",
            );
          }
          if (pendingChange) {
            e.preventDefault(); // prevent dropdown open on mobile
            e.stopPropagation();
            toast.error(
              "You cannot change your plan after already deciding to change it.",
            );
          }
        }}
        // disabled={userTappedCancel}
      >
        <option value="essential">Essential - 4.5 EUR</option>
        <option value="plus">Plus - 9.5 EUR</option>
        <option value="prime">Prime - 19.5 EUR</option>
        <option value="spotlight">Spotlight - 39.5 EUR</option>
      </select>
      {pendingChange && (
        <div className={`${styles.alert} ${styles.alertDowngrade}`}>
          <strong>Downgrade Scheduled:</strong> Your plan will change to{" "}
          {capitalize(pendingChange.new_tier_name || "the new plan")} on{" "}
          {new Date(pendingChange.effective_date * 1000).toLocaleDateString()}.
        </div>
      )}

      {upgrade && !pendingChange && (
        <div className={`${styles.alert} ${styles.alertUpgrade}`}>
          <strong>Upgrade:</strong> your card will be charged immediately for
          the plan difference.
        </div>
      )}
      {downgrade && !pendingChange && (
        <div className={`${styles.alert} ${styles.alertDowngrade}`}>
          <strong>Downgrade:</strong> changes take effect on your next billing
          date.
        </div>
      )}

      {!alreadyCanceled && (
        <div className={styles.statusWrapper}>
          <p className={styles.statusInfo}>
            No longer need this plan? You can cancel anytime. Your listing will
            remain active until the end of the billing period.
          </p>
          {!userTappedCancel ? (
            <button
              type="button"
              className={styles.cancelBtn}
              onClick={() => {
                if (plan !== listingData.listing_type) {
                  toast(
                    "You cannot cancel plan if you selected to change it.",
                    {
                      type: "error",
                    },
                  );
                  return;
                }
                setUserTappedCancelAction(true);
              }}
            >
              Cancel Plan
            </button>
          ) : (
            <button
              type="button"
              className={styles.activatePlanBtn}
              onClick={() => setUserTappedCancelAction(false)}
            >
              Keep Active
            </button>
          )}
        </div>
      )}
    </>
  );
}

function planRank(plan?: string): number {
  switch (plan?.toLowerCase()) {
    case "essential":
      return 1;
    case "plus":
      return 2;
    case "prime":
      return 3;
    case "spotlight":
      return 4;
    default:
      return 0;
  }
}
